import { CardContent, Stack } from '@mui/material';
import type { NetworkInterfaceConfig } from '@repo/api-gw-sdk';
import {
  NetworkInterfaceConfigModeEnum,
  SubnetConfigModeEnum,
  VNetConfigModeEnum,
} from '@repo/api-gw-sdk';
import { useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { SELECT_CREATE_NEW } from '@/components/shared/autocompleteWithNew';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { useValidateNetwork } from './azureVmConfiguration';
import { EonTagRemark } from './eonTagRemark';
import { SingleAzureDiskConfig } from './singleAzureDiskConfig';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreAzureVMState } from '../restoreAzureVMWizard';

function buildNetworkInterfaceConfig(
  state: RestoreAzureVMState
): NetworkInterfaceConfig | undefined {
  if (
    state.networkInterface !== SELECT_CREATE_NEW ||
    !state.virtualNetworkName ||
    !state.subnetName
  ) {
    return undefined;
  }
  return {
    mode: NetworkInterfaceConfigModeEnum.Create,
    vnet: {
      mode:
        state.virtualNetworkName === SELECT_CREATE_NEW
          ? VNetConfigModeEnum.Create
          : VNetConfigModeEnum.Existing,
      name:
        state.virtualNetworkName === SELECT_CREATE_NEW
          ? state.newVirtualNetworkName
          : state.virtualNetworkName,
    },
    subnet: {
      mode:
        state.subnetName === SELECT_CREATE_NEW
          ? SubnetConfigModeEnum.Create
          : SubnetConfigModeEnum.Existing,
      name:
        state.subnetName === SELECT_CREATE_NEW
          ? state.newSubnetName
          : state.subnetName,
    },
  };
}

export const AzureVmDiskConfiguration = (
  props: StepProps<RestoreAzureVMState>
) => {
  const dal = useDAL();
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const validNetwork = useValidateNetwork(
    props.wizardState.virtualNetworkName,
    props.wizardState.newVirtualNetworkName,
    props.wizardState.subnetName,
    props.wizardState.newSubnetName,
    props.wizardState.networkInterface
  );

  const startRestore = async () => {
    setIsRestoring(true);
    // TODO: Add validation against azure that configuration is correct and valid
    setError(undefined);
    const tags = {
      ...(props.wizardState.keepOriginalTags
        ? props.wizardState.initialTags
        : props.wizardState.tags),
      'eon:restore': 'true',
    };
    const diskSettings = props.wizardState.disksState?.map((disk) => ({
      providerDiskId: disk.diskSettings.providerDiskId,
      settings: {
        name: disk.diskSettings.settings.name,
        type: disk.diskSettings.settings.type,
        tier: disk.diskSettings.settings.tier,
        sizeBytes: disk.diskSettings.settings.sizeBytes,
        hyperVGeneration: disk.diskSettings.settings.hyperVGeneration,
        tags: {
          ...(props.wizardState.keepOriginalTags
            ? disk.initialTags
            : disk.tags),
          'eon:restore': 'true',
        },
      },
    }));

    const networkInterfaceConfig = buildNetworkInterfaceConfig(
      props.wizardState
    );

    return dal.restore.azureVm
      .restore(props.wizardState.resourceId, props.wizardState.snapshot.id, {
        restoreAccountId: props.wizardState.restoreAccount!.id,
        destination: {
          azureVm: {
            region: props.wizardState.location!,
            vmName: props.wizardState.vmName ?? '',
            resourceGroupName: props.wizardState.resourceGroup!,
            vmSize: props.wizardState.keepOriginalVMSize
              ? (props.wizardState.originalVMSize ?? '')
              : (props.wizardState.vmSize ?? ''),
            tags,
            // TODO: Implement UI for these fields using azureVMTypes and azureDiskTypes
            disks: diskSettings,
            startInstanceAfterRestore:
              props.wizardState.startInstanceAfterRestore,
            ...(networkInterfaceConfig
              ? { networkInterfaceConfig }
              : {
                  networkInterface: props.wizardState.networkInterface ?? '',
                }),
          },
        },
      })
      .then(() =>
        props.finish(`Started restore job for ${props.wizardState.vmName}`)
      )
      .catch(async (e) => {
        const errorMessage = await extractErrorMessage(e);
        setError(`Failed to restore disk: ${errorMessage}`);
      })
      .finally(() => {
        setIsRestoring(false);
      });
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark />
        )
      }
      stepperLabels={props.stepperLabels}
      stepperIndex={2}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={() =>
        !isRestoring &&
        !!props.wizardState.vmName &&
        !!props.wizardState.resourceGroup &&
        !!props.wizardState.location &&
        validNetwork &&
        (props.wizardState.keepOriginalVMSize ||
          Boolean(props.wizardState.vmSize))
      }
      onNextClick={startRestore}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <CardContent className='p-0'>
        {props.wizardState.disksState?.map((disk) => {
          const initialDiskSettings = props.wizardState.initialDiskStates.find(
            (d) =>
              disk.diskSettings.providerDiskId === d.diskSettings.providerDiskId
          );
          if (
            !initialDiskSettings ||
            !props.wizardState.restoreAccount ||
            !props.wizardState.resourceGroup ||
            !props.wizardState.location
          ) {
            return null;
          }
          return (
            <SingleAzureDiskConfig
              key={disk.diskSettings.providerDiskId}
              diskId={disk.diskSettings.providerDiskId}
              accountID={props.wizardState.restoreAccount.id}
              location={props.wizardState.location}
              initialSettings={initialDiskSettings}
              diskSettings={disk}
              setWizardState={props.setWizardState}
            />
          );
        })}
      </CardContent>
    </StepContainer>
  );
};
